import { useRef, useEffect, useMemo, useState } from 'react';

const PARTICLE_SEEDS = [
  { left: 32, top: 22, size: 1.2, delay: 0.2, dur: 7 },
  { left: 45, top: 18, size: 2.0, delay: 1.8, dur: 9 },
  { left: 58, top: 28, size: 1.5, delay: 3.4, dur: 6 },
  { left: 38, top: 35, size: 2.5, delay: 0.7, dur: 8 },
  { left: 52, top: 15, size: 1.0, delay: 5.2, dur: 10 },
  { left: 48, top: 42, size: 1.8, delay: 2.1, dur: 6 },
  { left: 62, top: 20, size: 1.3, delay: 4.5, dur: 7 },
  { left: 35, top: 50, size: 2.2, delay: 6.0, dur: 9 },
  { left: 55, top: 32, size: 0.8, delay: 1.2, dur: 6 },
  { left: 42, top: 25, size: 2.8, delay: 3.8, dur: 8 },
  { left: 68, top: 38, size: 1.6, delay: 7.1, dur: 7 },
  { left: 30, top: 45, size: 1.1, delay: 2.5, dur: 9 },
  { left: 50, top: 12, size: 2.0, delay: 5.8, dur: 6 },
  { left: 60, top: 48, size: 1.4, delay: 0.9, dur: 8 },
  { left: 40, top: 30, size: 3.0, delay: 4.0, dur: 9 },
  { left: 37, top: 55, size: 1.7, delay: 6.5, dur: 6 },
  { left: 53, top: 22, size: 0.9, delay: 3.1, dur: 7 },
  { left: 65, top: 25, size: 1.9, delay: 1.5, dur: 8 },
  { left: 44, top: 60, size: 1.3, delay: 7.8, dur: 6 },
  { left: 57, top: 40, size: 2.1, delay: 2.9, dur: 9 },
  { left: 33, top: 18, size: 2.3, delay: 5.5, dur: 10 },
  { left: 49, top: 52, size: 1.0, delay: 0.4, dur: 7 },
  { left: 63, top: 14, size: 1.8, delay: 4.8, dur: 8 },
  { left: 41, top: 38, size: 2.6, delay: 8.2, dur: 6 },
  { left: 54, top: 58, size: 1.5, delay: 3.6, dur: 9 },
];

const LASER_H_LINES = [
  { top: 28, delay: 0, dur: 4.5 },
  { top: 35, delay: 1.2, dur: 5.0 },
  { top: 42, delay: 2.5, dur: 4.2 },
  { top: 49, delay: 0.8, dur: 5.5 },
  { top: 56, delay: 3.1, dur: 4.8 },
  { top: 63, delay: 1.9, dur: 5.2 },
  { top: 70, delay: 3.8, dur: 4.6 },
  { top: 77, delay: 2.2, dur: 5.3 },
];

const LASER_V_LINES = [
  { left: 22, delay: 0, dur: 5.5 },
  { left: 38, delay: 1.8, dur: 4.8 },
  { left: 54, delay: 2.9, dur: 5.2 },
  { left: 70, delay: 0.6, dur: 5.8 },
  { left: 78, delay: 3.5, dur: 4.5 },
];

const LASER_DOTS = [
  { left: 15, top: 28, delay: 0 },
  { left: 15, top: 35, delay: 1.4 },
  { left: 85, top: 42, delay: 0.7 },
  { left: 85, top: 56, delay: 2.1 },
  { left: 15, top: 63, delay: 1.1 },
  { left: 85, top: 70, delay: 2.8 },
  { left: 15, top: 77, delay: 3.2 },
  { left: 85, top: 28, delay: 1.9 },
  { left: 22, top: 14, delay: 0.3 },
  { left: 78, top: 14, delay: 2.4 },
];

const CEILING_INDICATORS = [
  { left: 15, delay: 0 },
  { left: 30, delay: 1.2 },
  { left: 50, delay: 0.5 },
  { left: 70, delay: 2.1 },
  { left: 85, delay: 1.6 },
];

const SCAN_LINES = [
  { delay: 0, dur: 4 },
  { delay: 1.3, dur: 3.5 },
  { delay: 2.6, dur: 4.2 },
];

const IMAGES = [
  'https://readdy.ai/api/search-image?query=White%20gloved%20hands%20carefully%20holding%20a%20luxury%20cardboard%20package%20box%2C%20close%20up%20professional%20photography%2C%20dramatic%20warm%20gold%20and%20amber%20lighting%2C%20dark%20blurred%20background%2C%20premium%20concierge%20delivery%20aesthetic%2C%20editorial%20style%2C%20sharp%20focus%20on%20gloves%20and%20box&width=1920&height=1080&seq=hero-img-1&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Luxurious%20gold%20bank%20vault%20interior%20with%20rows%20of%20safe%20deposit%20boxes%2C%20warm%20golden%20ambient%20lighting%20reflecting%20off%20brass%20and%20polished%20metal%20surfaces%2C%20dramatic%20perspective%20depth%2C%20secure%20storage%20facility%2C%20cinematic%20dark%20moody%20atmosphere%20with%20gold%20highlights&width=1920&height=1080&seq=hero-img-2&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Professional%20courier%20in%20a%20sharp%20dark%20suit%20handing%20a%20sealed%20document%20envelope%20to%20a%20businessman%20at%20a%20luxury%20office%20reception%20desk%2C%20warm%20dramatic%20side%20lighting%2C%20dark%20moody%20background%20with%20soft%20bokeh%2C%20premium%20white%20glove%20delivery%20service%20aesthetic%2C%20editorial%20photography&width=1920&height=1080&seq=hero-img-3&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Black%20rugged%20Pelican%20hard%20protective%20equipment%20case%20open%20on%20a%20dark%20surface%20revealing%20custom%20foam%20cutouts%20holding%20professional%20electronics%20gear%20cameras%20lenses%20and%20surveillance%20equipment%20inside%2C%20dramatic%20warm%20amber%20and%20gold%20side%20lighting%20casting%20long%20shadows%20across%20the%20case%20interior%2C%20dark%20moody%20background%20with%20cinematic%20atmosphere%2C%20premium%20high%20security%20logistics%20and%20secure%20equipment%20transport%20editorial%20product%20photography%2C%20sharp%20focus%20on%20case%20details%20and%20electronics&width=1920&height=1080&seq=hero-img-4&orientation=landscape',
];

const CROSSFADE_MS = 1500;
const SLIDE_INTERVAL_MS = 5000;

export default function Hero() {
  const [xState, setXState] = useState<{ current: number; fading: boolean; snap: boolean }>({
    current: 0,
    fading: false,
    snap: false,
  });
  const idxRef = useRef(0);
  const bgRef = useRef<HTMLDivElement>(null);
  const leftWallRef = useRef<HTMLDivElement>(null);
  const rightWallRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const laserGridRef = useRef<HTMLDivElement>(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const targetRef = useRef({ x: 0, y: 0 });

  const particles = useMemo(() => PARTICLE_SEEDS, []);

  // Crossfade images
  useEffect(() => {
    const timer = setInterval(() => {
      const cur = idxRef.current;
      const nxt = (cur + 1) % IMAGES.length;

      // Start fade: transition is already on both images from idle state
      setXState({ current: cur, fading: true, snap: false });

      setTimeout(() => {
        idxRef.current = nxt;
        // Snap: kill transition so opacities reset instantly, then re-enable
        setXState({ current: nxt, fading: false, snap: true });
        requestAnimationFrame(() => {
          setXState({ current: nxt, fading: false, snap: false });
        });
      }, CROSSFADE_MS);
    }, SLIDE_INTERVAL_MS);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      targetRef.current = {
        x: (e.clientX / window.innerWidth - 0.5) * 2,
        y: (e.clientY / window.innerHeight - 0.5) * 2,
      };
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });

    let rafId: number;
    const animate = () => {
      mouseRef.current.x += (targetRef.current.x - mouseRef.current.x) * 0.03;
      mouseRef.current.y += (targetRef.current.y - mouseRef.current.y) * 0.03;

      const { x, y } = mouseRef.current;

      if (bgRef.current) {
        bgRef.current.style.transform = `translate(${x * -35}px, ${y * -22}px)`;
      }
      if (leftWallRef.current) {
        leftWallRef.current.style.transform = `translateX(${x * -25}px)`;
      }
      if (rightWallRef.current) {
        rightWallRef.current.style.transform = `translateX(${x * 25}px)`;
      }
      if (laserGridRef.current) {
        laserGridRef.current.style.transform = `translate(${x * -8}px, ${y * -6}px)`;
      }
      if (contentRef.current) {
        contentRef.current.style.transform = `translate(${x * 8}px, ${y * 8}px)`;
      }

      rafId = requestAnimationFrame(animate);
    };
    rafId = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <section className="relative w-full h-screen min-h-[700px] overflow-hidden bg-black">

      {/* Auto-dolly zoom wrapper */}
      <div
        className="absolute inset-0"
        style={{ animation: 'dolly-zoom 30s ease-in-out infinite alternate' }}
      >
        {/* Vault hallway background with mouse parallax */}
        <div ref={bgRef} className="absolute inset-0 will-change-transform">
          {/* Current image — fades out during transition */}
          <img
            src={IMAGES[xState.current]}
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center"
            style={{
              opacity: xState.fading ? 0 : 1,
              transition: xState.snap ? 'none' : 'opacity 1.5s ease-in-out',
            }}
          />
          {/* Next image — fades in during transition */}
          <img
            src={IMAGES[(xState.current + 1) % IMAGES.length]}
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center"
            style={{
              opacity: xState.fading ? 1 : 0,
              transition: xState.snap ? 'none' : 'opacity 1.5s ease-in-out',
            }}
          />
          {/* Cinematic color grade: teal shadows + warm gold highlights */}
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-950/20 via-transparent to-amber-600/10"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/70"></div>
        </div>
      </div>

      {/* Mission Impossible red laser grid */}
      <div ref={laserGridRef} className="absolute inset-0 pointer-events-none z-[3] will-change-transform">
        {LASER_H_LINES.map((laser, i) => (
          <div key={`h-${i}`} className="absolute left-0 right-0" style={{ top: `${laser.top}%` }}>
            <div
              className="absolute left-0 right-0 h-[1px]"
              style={{
                background: 'linear-gradient(90deg, transparent 5%, rgba(239,68,68,0.15) 15%, rgba(239,68,68,0.6) 50%, rgba(239,68,68,0.15) 85%, transparent 95%)',
                animation: `laser-sweep-h ${laser.dur}s ${laser.delay}s infinite ease-in-out`,
              }}
            />
            <div
              className="absolute left-0 right-0 h-[2px] blur-[2px]"
              style={{
                background: 'linear-gradient(90deg, transparent 5%, rgba(239,68,68,0.08) 15%, rgba(239,68,68,0.3) 50%, rgba(239,68,68,0.08) 85%, transparent 95%)',
                animation: `laser-sweep-h ${laser.dur}s ${laser.delay}s infinite ease-in-out`,
              }}
            />
          </div>
        ))}
        {LASER_V_LINES.map((laser, i) => (
          <div key={`v-${i}`} className="absolute top-0 bottom-0" style={{ left: `${laser.left}%` }}>
            <div
              className="absolute top-0 bottom-0 w-[1px]"
              style={{
                background: 'linear-gradient(180deg, transparent 5%, rgba(239,68,68,0.15) 15%, rgba(239,68,68,0.5) 50%, rgba(239,68,68,0.15) 85%, transparent 95%)',
                animation: `laser-sweep-v ${laser.dur}s ${laser.delay}s infinite ease-in-out`,
              }}
            />
            <div
              className="absolute top-0 bottom-0 w-[2px] blur-[2px]"
              style={{
                background: 'linear-gradient(180deg, transparent 5%, rgba(239,68,68,0.06) 15%, rgba(239,68,68,0.25) 50%, rgba(239,68,68,0.06) 85%, transparent 95%)',
                animation: `laser-sweep-v ${laser.dur}s ${laser.delay}s infinite ease-in-out`,
              }}
            />
          </div>
        ))}
      </div>

      {/* Laser emitter dots on walls */}
      {LASER_DOTS.map((dot, i) => (
        <div
          key={`ld-${i}`}
          className="absolute w-[5px] h-[5px] rounded-full bg-red-500 pointer-events-none z-[3]"
          style={{
            left: `${dot.left}%`,
            top: `${dot.top}%`,
            animation: `laser-dot-blink 2.5s ${dot.delay}s infinite ease-in-out`,
          }}
        />
      ))}

      {/* Security camera scan lines */}
      {SCAN_LINES.map((sl, i) => (
        <div
          key={`scan-${i}`}
          className="absolute left-0 right-0 pointer-events-none z-[2]"
          style={{ animation: `scan-lines-down ${sl.dur}s ${sl.delay}s infinite linear` }}
        >
          <div className="h-[1px] bg-gradient-to-r from-transparent via-cyan-400/15 to-transparent"></div>
        </div>
      ))}

      {/* Left vault wall */}
      <div ref={leftWallRef} className="absolute left-0 top-0 bottom-0 w-[11%] will-change-transform z-[2]">
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-transparent"></div>
        <div className="absolute right-0 top-0 bottom-0 w-[1px] bg-gradient-to-b from-accent-500/0 via-accent-500/35 to-accent-500/0"></div>
      </div>

      {/* Right vault wall */}
      <div ref={rightWallRef} className="absolute right-0 top-0 bottom-0 w-[11%] will-change-transform z-[2]">
        <div className="absolute inset-0 bg-gradient-to-l from-black/85 via-black/55 to-transparent"></div>
        <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-gradient-to-b from-accent-500/0 via-accent-500/35 to-accent-500/0"></div>
      </div>

      {/* Ceiling security indicators */}
      <div className="absolute top-0 left-0 right-0 h-[4px] z-[3]">
        {CEILING_INDICATORS.map((ind) => (
          <div
            key={ind.left}
            className="absolute top-0 w-[3px] h-[3px] rounded-full bg-accent-500"
            style={{
              left: `${ind.left}%`,
              animation: `vault-indicator-blink 3s ${ind.delay}s infinite ease-in-out`,
            }}
          />
        ))}
      </div>

      {/* Dramatic sweeping light beam */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-[1]">
        <div
          className="absolute top-0 -left-[20%] w-[55%] h-[200%] bg-gradient-to-b from-accent-500/0 via-accent-500/5 to-accent-500/0"
          style={{ animation: 'light-sweep 15s ease-in-out infinite' }}
        ></div>
      </div>

      {/* Floating gold dust particles */}
      {particles.map((p, i) => (
        <div
          key={i}
          className="absolute rounded-full pointer-events-none z-[1]"
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            background: `oklch(var(--accent-500) / ${0.2 + (p.size / 10)})`,
            animation: `float-up ${p.dur}s ${p.delay}s infinite ease-in-out`,
            boxShadow: p.size > 2
              ? `0 0 ${p.size * 3}px ${p.size}px oklch(var(--accent-500) / 0.25)`
              : 'none',
          }}
        />
      ))}

      {/* Floor bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-[22%] bg-gradient-to-t from-black/75 via-black/30 to-transparent pointer-events-none z-[2]"></div>

      {/* Breathing vignette */}
      <div
        className="absolute inset-0 pointer-events-none z-[1]"
        style={{
          animation: 'vignette-breathe 8s ease-in-out infinite',
          background: 'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.5) 85%, rgba(0,0,0,0.75) 100%)',
        }}
      ></div>

      {/* Content overlay */}
      <div
        ref={contentRef}
        className="relative z-10 flex flex-col justify-between h-full px-6 md:px-10 lg:px-16 pt-32 pb-16 md:pb-20 will-change-transform"
      >
        <div className="flex flex-col justify-center flex-1">
          <div className="w-full max-w-5xl">
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-heading font-bold text-white leading-[1.05] tracking-tight">
              International<br />
              High-Value<br />
              Secure Transit
            </h1>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-8 md:gap-16 lg:gap-24">
          <div className="md:w-1/2">
            <p className="text-white/85 text-sm md:text-base leading-relaxed max-w-md">
              Licensed, bonded, and fully insured international courier service for documents, electronics, legal papers, medical samples where permitted, and high-value items requiring white-glove delivery coordination worldwide.
            </p>
          </div>
          <div className="md:w-1/2 md:pt-6">
            <p className="text-white/85 text-sm md:text-base leading-relaxed max-w-md">
              Armed transport, armored partners, and secure vaulting capabilities for jewelry, precious metals, watches, fine art, and cash-equivalent shipments with the highest declared-value insurance available.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}